import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  MessageSquare, Send, Loader2, Trash2, Clock, AtSign
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_COLORS = {
  admin: "bg-emerald-100 text-emerald-700 border-emerald-200",
  borrower: "bg-blue-100 text-blue-700 border-blue-200",
  lender: "bg-purple-100 text-purple-700 border-purple-200",
  investor: "bg-amber-100 text-amber-700 border-amber-200",
};

const MENTION_ROLE_DOT = {
  admin: "bg-emerald-500",
  borrower: "bg-blue-500",
  lender: "bg-purple-500",
  investor: "bg-amber-500",
};

function renderWithMentions(text) {
  const parts = text.split(/(@\w[\w\s]*?\b)/g);
  return parts.map((part, i) =>
    part.startsWith("@") ? (
      <span key={i} className="text-emerald-600 font-semibold bg-emerald-50 px-0.5 rounded">{part}</span>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

export default function EntityNotes({ entityType, entityId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mentions, setMentions] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionResults, setMentionResults] = useState([]);
  const [mentionIdx, setMentionIdx] = useState(0);
  const inputRef = useRef(null);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchNotes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/entity/notes/${entityType}/${entityId}`, { headers });
      setNotes(res.data.notes || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [entityType, entityId]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  // Mention search
  useEffect(() => {
    if (mentionQuery === null || mentionQuery.length < 1) {
      setMentionResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/entity/users/mention-search?q=${encodeURIComponent(mentionQuery)}`, { headers });
        setMentionResults(res.data.users || []);
        setMentionIdx(0);
      } catch { setMentionResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [mentionQuery]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setNewNote(val);
    // Detect @mention
    const cursor = e.target.selectionStart;
    const before = val.slice(0, cursor);
    const match = before.match(/@(\w*)$/);
    if (match) {
      setMentionQuery(match[1]);
    } else {
      setMentionQuery(null);
      setMentionResults([]);
    }
  };

  const insertMention = (user) => {
    const cursor = inputRef.current?.selectionStart || newNote.length;
    const before = newNote.slice(0, cursor);
    const after = newNote.slice(cursor);
    const atIdx = before.lastIndexOf("@");
    const updated = before.slice(0, atIdx) + `@${user.full_name} ` + after;
    setNewNote(updated);
    setMentions(prev => [...prev.filter(m => m.user_id !== user.user_id), { user_id: user.user_id, full_name: user.full_name }]);
    setMentionQuery(null);
    setMentionResults([]);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (mentionResults.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setMentionIdx(i => Math.min(i + 1, mentionResults.length - 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setMentionIdx(i => Math.max(i - 1, 0)); }
      else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); insertMention(mentionResults[mentionIdx]); }
      else if (e.key === "Escape") { setMentionQuery(null); setMentionResults([]); }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newNote.trim() || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/entity/notes/${entityType}/${entityId}`, {
        content: newNote.trim(),
        mentions: mentions.map(m => ({ user_id: m.user_id, full_name: m.full_name }))
      }, { headers });
      setNewNote("");
      setMentions([]);
      fetchNotes();
    } catch (err) { console.error(err); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (noteId) => {
    try {
      await axios.delete(`${API}/entity/notes/${noteId}`, { headers });
      fetchNotes();
    } catch (err) { console.error(err); }
  };

  return (
    <div className="rounded-xl border border-[#e8e0d0] bg-white" data-testid="entity-notes">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[#e8e0d0]">
        <MessageSquare className="h-4 w-4 text-emerald-600" />
        <span className="text-xs font-bold text-[#333] uppercase tracking-wider">Notes & Activity ({notes.length})</span>
      </div>

      <div className="p-4 space-y-3">
        {/* Add note form with @mention */}
        <form onSubmit={handleSubmit} className="relative" data-testid="notes-form">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input ref={inputRef} type="text" value={newNote}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Add a note... use @ to mention"
                className="w-full px-3 py-2 rounded-lg border border-[#e8e0d0] text-sm focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none bg-[#faf7f2]"
                data-testid="notes-input" />

              {/* Mention suggestions dropdown */}
              {mentionResults.length > 0 && (
                <div className="absolute left-0 right-0 bottom-full mb-1 rounded-lg border border-[#e8e0d0] bg-white shadow-lg z-20 max-h-40 overflow-y-auto"
                  data-testid="mention-dropdown">
                  {mentionResults.map((u, i) => (
                    <button key={u.user_id} type="button"
                      onClick={() => insertMention(u)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${
                        i === mentionIdx ? "bg-emerald-50" : "hover:bg-[#faf7f2]"
                      }`}
                      data-testid={`mention-option-${i}`}>
                      <div className={`w-2 h-2 rounded-full ${MENTION_ROLE_DOT[u.role] || "bg-gray-400"}`} />
                      <span className="text-sm text-[#333] font-medium">{u.full_name}</span>
                      <span className="text-[10px] text-[#999] capitalize">{u.role}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button type="submit" disabled={submitting || !newNote.trim()}
              className="px-3 py-2 rounded-lg bg-emerald-700 text-white hover:bg-emerald-600 disabled:opacity-40 transition-colors"
              data-testid="notes-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
          {/* Mention tags */}
          {mentions.length > 0 && (
            <div className="flex gap-1 mt-1.5 flex-wrap">
              {mentions.map(m => (
                <span key={m.user_id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] text-emerald-700 font-medium"
                  data-testid={`mention-tag-${m.user_id}`}>
                  <AtSign className="h-2.5 w-2.5" />{m.full_name}
                  <button type="button" onClick={() => setMentions(prev => prev.filter(x => x.user_id !== m.user_id))}
                    className="ml-0.5 text-emerald-400 hover:text-red-500">x</button>
                </span>
              ))}
            </div>
          )}
        </form>

        {/* Notes list */}
        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>
        ) : notes.length === 0 ? (
          <p className="text-xs text-[#999] text-center py-3">No notes yet. Add the first one!</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {notes.map((n, i) => (
              <motion.div key={n.note_id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="rounded-lg bg-[#faf7f2] border border-[#e8e0d0] p-3"
                data-testid={`note-item-${n.note_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#333]">{renderWithMentions(n.content)}</p>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${ROLE_COLORS[n.author_role] || "bg-gray-100 text-gray-600"}`}>
                        {n.author_name || "Unknown"}
                      </span>
                      <span className="text-[9px] text-[#bbb] flex items-center gap-0.5">
                        <Clock className="h-2.5 w-2.5" />
                        {n.created_at ? new Date(n.created_at).toLocaleString() : ""}
                      </span>
                      {n.is_own && <span className="text-[9px] text-blue-500 font-medium">You</span>}
                      {n.mentions && n.mentions.length > 0 && (
                        <span className="text-[9px] text-emerald-500 flex items-center gap-0.5">
                          <AtSign className="h-2.5 w-2.5" />{n.mentions.length} mentioned
                        </span>
                      )}
                    </div>
                  </div>
                  {n.is_own && (
                    <button onClick={() => handleDelete(n.note_id)}
                      className="p-1 rounded hover:bg-red-50 text-[#ccc] hover:text-red-400 transition-colors shrink-0"
                      data-testid={`note-delete-${n.note_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
